// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ArtworkRegionCitationLink.test.tsx
import { type ArtworkRegionCitation } from "@mg/schemas/src/commons/ticket";

/** One place on one artwork that an answer cites: every classified line
 * printed in that box (a German, a French and a Dutch claim in one paragraph
 * are one spot), or the file alone for a printed text match with no box. */
export interface PackSpot {
  key: string;
  citations: ArtworkRegionCitation[];
}

/** One pack (artwork) an answer cites, with its spots and what they hold. */
export interface CitedPack {
  sourceId: string;
  label: string;
  pages: number[];
  spots: PackSpot[];
  /** Distinct printed claims (a wording counted once however many values it was classified on). */
  claims: number;
  exact: number;
  possible: number;
  /** Distinct printed text matches: the words themselves, neither exact nor possible. */
  printed: number;
  /** Distinct marks. */
  logos: number;
}

type PackStatus = ArtworkRegionCitation["status"];

// why a Record and not a switch: a Record over every status (or list) fails
// `tsc` when a fourth one is added, as an exhaustive switch does, and has no
// `default` arm that no input can reach.
const STATUS_KEYS: Record<
  PackStatus,
  | "packCitation.statusExact"
  | "packCitation.statusPossible"
  | "packCitation.statusPrinted"
> = {
  exact: "packCitation.statusExact",
  possible: "packCitation.statusPossible",
  printed: "packCitation.statusPrinted",
};

const KIND_KEYS: Record<
  ArtworkRegionCitation["listKind"],
  | "packCitation.kindTerritory"
  | "packCitation.kindClaimType"
  | "packCitation.kindText"
  | "packCitation.kindLogo"
> = {
  territory: "packCitation.kindTerritory",
  claim_type: "packCitation.kindClaimType",
  text: "packCitation.kindText",
  logo: "packCitation.kindLogo",
};

const COUNTED_AS: Record<PackStatus, "claim" | "printed"> = {
  exact: "claim",
  possible: "claim",
  printed: "printed",
};

/** The label of a pack citation's status; a fourth status fails `tsc` here
 * instead of being drawn as "Possible". */
export function packStatusKey(status: PackStatus) {
  return STATUS_KEYS[status];
}

/** The label of the list a pack citation's row is on. */
export function packKindKey(listKind: ArtworkRegionCitation["listKind"]) {
  return KIND_KEYS[listKind];
}

/** Which count a status adds to: a classified claim's, or a printed text
 * match's. */
function countedAs(status: PackStatus): "claim" | "printed" {
  return COUNTED_AS[status];
}

function spotKey(citation: ArtworkRegionCitation): string {
  if (!citation.region) return `${citation.sourceId}|file`;
  const { page, x, y, w, h } = citation.region;
  return `${citation.sourceId}|${page}|${x}|${y}|${w}|${h}`;
}

/**
 * warhol cites each classified row on its own, so one pack can arrive as many
 * citations, several of them on the same box. The footer shows one chip per
 * pack and one entry per box, in the order the answer cited them.
 *
 * why grouped here and not in the harvest: each stored citation keeps its own
 * value, status and confidence, which the dialog shows line by line; grouping
 * is how they are drawn, not what they say.
 */
export function groupPackCitations(
  citations: readonly ArtworkRegionCitation[],
): CitedPack[] {
  const packs = new Map<string, ArtworkRegionCitation[]>();
  for (const citation of citations) {
    const held = packs.get(citation.sourceId);
    if (held) held.push(citation);
    else packs.set(citation.sourceId, [citation]);
  }
  return Array.from(packs.entries()).map(([sourceId, held]) => {
    const spots = new Map<string, ArtworkRegionCitation[]>();
    for (const citation of held) {
      const key = spotKey(citation);
      const spot = spots.get(key);
      if (spot) spot.push(citation);
      else spots.set(key, [citation]);
    }
    // A printed claim classified on two values is one claim; it is exact when
    // any of its values is. A printed text match is counted on its own: it is
    // neither exact nor possible, so it must not read as an uncertain claim.
    const claimStatus = new Map<string, boolean>();
    const printed = new Set<string>();
    const marks = new Set<string>();
    for (const citation of held) {
      if (citation.listKind === "logo") {
        marks.add(spotKey(citation));
        continue;
      }
      const line = `${spotKey(citation)}|${citation.quote ?? ""}`;
      if (countedAs(citation.status) === "printed") {
        printed.add(line);
        continue;
      }
      claimStatus.set(
        line,
        (claimStatus.get(line) ?? false) || citation.status === "exact",
      );
    }
    const exact = Array.from(claimStatus.values()).filter(Boolean).length;
    return {
      sourceId,
      label: held[0].product?.trim() || held[0].fileName,
      pages: Array.from(
        new Set(held.flatMap((one) => (one.region ? [one.region.page] : []))),
      ).sort((a, b) => a - b),
      spots: Array.from(spots.entries()).map(([key, cited]) => ({
        key,
        citations: cited,
      })),
      claims: claimStatus.size,
      exact,
      possible: claimStatus.size - exact,
      printed: printed.size,
      logos: marks.size,
    };
  });
}
